
import React, { useState, useEffect, useCallback } from 'react';
import api from '../mainApi';

const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fmtTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const Attendance = () => {
  const today = toLocalDateString(new Date());
  const [selectedDate, setSelectedDate] = useState(today);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAttendance = useCallback(() => {
    setLoading(true);
    api.get(`/attendance/by-date?date=${selectedDate}`)
      .then(res => setMembers(res.data))
      .catch(() => showToast('Failed to load attendance.', 'danger'))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const presentCount = members.filter(m => m.is_present === 1).length;
  const absentCount = members.length - presentCount;

  return (
    <div className="container-fluid mt-4">

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0 fw-bold">Attendance Register</h2>
          <small className="text-muted">Members punch in/out via the Android app</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <input
            type="date"
            className="form-control"
            style={{ width: 'auto' }}
            value={selectedDate}
            max={today}
            onChange={e => setSelectedDate(e.target.value)}
          />
          <button className="btn btn-outline-primary btn-sm" onClick={fetchAttendance}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-sm-4">
          <div className="card border-0 shadow-sm text-center py-3">
            <div className="fs-2 fw-bold text-primary">{members.length}</div>
            <div className="text-muted small">Total Members</div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card border-0 shadow-sm text-center py-3">
            <div className="fs-2 fw-bold text-success">{presentCount}</div>
            <div className="text-muted small">Present (Punched In)</div>
          </div>
        </div>
        <div className="col-sm-4">
          <div className="card border-0 shadow-sm text-center py-3">
            <div className="fs-2 fw-bold text-danger">{absentCount}</div>
            <div className="text-muted small">Absent</div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`alert alert-${toast.type} py-2`} role="alert">{toast.msg}</div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2 text-muted">Loading attendance…</div>
        </div>
      ) : (
        <div className="card shadow-sm border-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Punch In</th>
                  <th className="text-center">Punch Out</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">No members found.</td>
                  </tr>
                ) : members.map((m, idx) => (
                  <tr key={m.member_id}>
                    <td className="text-muted">{idx + 1}</td>
                    <td className="fw-semibold">{m.name}</td>
                    <td className="text-muted">{m.email}</td>
                    <td className="text-center">
                      {m.is_present === 1
                        ? <span className="badge bg-success">Present</span>
                        : <span className="badge bg-danger">Absent</span>
                      }
                    </td>
                    <td className="text-center text-muted small">{fmtTime(m.punch_in_time)}</td>
                    <td className="text-center text-muted small">{fmtTime(m.punch_out_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;